'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';

import fig from './figureCss.module.css';
import styles from './AskFigure.module.css';
import type { AskFigureData, AskFigureMeta } from './askArticles';
import type { AskCategoryId } from './askData';

/* One figure inside a question.
 *
 * The chrome — head, ask panel, plot mount, source line — is BUILT here from
 * the figure's fields. It used to be the mockup's markup rendered verbatim,
 * which worked but could not survive a CMS: an editor would have been
 * authoring HTML, and the runtime finds its hooks by class name, so a typo
 * would produce a figure that draws and never answers.
 *
 * So the class names the runtime queries — .uhfig-plot, .uhfig-ask,
 * input[data-key], .ask-seg, .uhfig-ans — are written in exactly one place,
 * here, and the panel's shape comes from data (meta.controls). Change a class
 * name in figureRuntime.js and this file is the only one that has to follow.
 * Those names are also why figureCss.module.css wraps every selector in
 * :global(): the runtime writes them onto nodes it creates itself, where a
 * hashed module class can never land.
 *
 * The ask panel ships hidden, as the source did: the runtime reveals it only
 * once something can answer it, so a figure whose form has no handler shows
 * its finding and stays quiet rather than taking a number and returning
 * nothing.
 *
 * React renders this subtree once and never re-renders it — the runtime owns
 * everything inside .uhfig-plot and mutates the DOM directly, and the
 * segmented controls are uncontrolled because the runtime toggles their
 * aria-pressed itself. The mount effect returns the runtime's teardown.
 */

/* "ALT: Scatter plot of ApoB on the vertical axis…" — the label leaked into
   the value. All 19 figures carry it, in the CMS and in the vendored extract
   alike, so it is live: printed under every figure as visible body text and
   read out as the plot's aria-label, where a screen reader says "A L T colon"
   before the description it was meant to introduce. Stripped here rather than
   in either source, because both sources have it. */
const describe = (alt: string | null | undefined): string | undefined =>
  alt?.replace(/^\s*ALT\s*:\s*/i, '').trim() || undefined;

const AskFigure = ({
  meta,
  data,
  category,
}: {
  meta: AskFigureMeta;
  data?: AskFigureData;
  category: AskCategoryId;
}) => {
  const t = useTranslations('science');
  const hostRef = useRef<HTMLDivElement>(null);

  /* The renderers draw once and hold no layout state, so a breakpoint change
     is a re-draw rather than a reflow. Watched with matchMedia rather than a
     resize listener: this fires twice per crossing, not on every pixel. */
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const sync = () => setNarrow(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !data) return undefined;

    let dispose: (() => void) | undefined;
    let cancelled = false;

    /* Loaded on demand. The runtime is 64KB and only question pages need it,
       so it stays out of the shared bundle the rest of /science pays for. */
    import('./figureRuntime')
      .then(({ mountAskFigures }) => {
        if (cancelled) return;
        dispose = mountAskFigures(host, { [meta.id]: data }, { narrow });
      })
      .catch((err) => {
        /* Loud in development, silent for the reader: the figure's head, its
           caption and its ALT text all still describe the finding, so a
           failed plot degrades to a figure without a picture. */
        if (process.env.NODE_ENV !== 'production') {
          console.error(`[ask] figure runtime failed for ${meta.id}`, err);
        }
      });

    return () => {
      cancelled = true;
      if (dispose) dispose();
    };
  }, [data, meta.id, narrow]);

  /* Tap-away dismisses, on touch only.
   *
   * The renderers open a readout on pointerenter and close it on
   * pointerleave. A mouse always leaves; a finger does not — tapping a bar
   * fires enter and nothing ever fires leave, so the readout sticks and the
   * other marks stay dimmed behind it until the reader happens to tap another
   * mark. Tapping anywhere off the figure now puts it back.
   *
   * Only the visual state is reset, and deliberately: the ask marker stays,
   * because it reflects a number the reader typed rather than something they
   * are pointing at. */
  useEffect(() => {
    if (!narrow) return undefined;
    const host = hostRef.current;
    if (!host) return undefined;

    const onDown = (e: PointerEvent) => {
      const figEl = host.querySelector('.uhfig');
      if (!figEl || figEl.contains(e.target as Node)) return;
      host
        .querySelectorAll('.uhfig-tip.on, .uhfig-read.on')
        .forEach((n) => n.classList.remove('on'));
      host
        .querySelectorAll('.mk.dim')
        .forEach((n) => n.classList.remove('dim'));
      host
        .querySelectorAll('.xhair, .xchip')
        .forEach((n) => n.setAttribute('opacity', '0'));
    };

    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [narrow, data, meta.id]);

  /* Guarded here too, not only at the CMS boundary. This reads
     `controls.controls.map` a few lines down, and a shape that satisfies the
     type but carries no array is exactly what arrived from the CMS — so the
     component should not be the thing that discovers it. */
  const controls =
    meta.controls &&
    Array.isArray(meta.controls.controls) &&
    meta.controls.controls.length
      ? meta.controls
      : null;

  return (
    <figure className={styles.figure}>
      <div ref={hostRef} className={fig.figHost}>
        <div className="uhfig" data-fig={meta.id} data-cat={category}>
          <div className="uhfig-head">
            <div>
              {meta.kick ? <p className="uhfig-kick">{meta.kick}</p> : null}
              {meta.title ? <p className="uhfig-title">{meta.title}</p> : null}
              {meta.sub ? <p className="uhfig-sub">{meta.sub}</p> : null}
            </div>
            {meta.hintHover || meta.hintTap ? (
              <span className="uhfig-hint" aria-hidden="true">
                <i />
                <span className="h-hov">{meta.hintHover}</span>
                <span className="h-tap">{meta.hintTap}</span>
              </span>
            ) : null}
          </div>

          {controls ? (
            /* hidden until the runtime can answer — see the note above */
            <div className="uhfig-ask" hidden>
              <div className="ask-top">
                <p className="ask-lab">
                  <i />
                  {t('science.ask.heading')}
                </p>
                {controls.privacy ? (
                  <span className="ask-priv">{controls.privacy}</span>
                ) : null}
              </div>

              <div className="ask-row">
                {controls.controls.map((c) =>
                  c.kind === 'number' ? (
                    <label className="ask-field" key={c.key}>
                      <span>{c.label}</span>
                      <span className="ask-in">
                        <input
                          type="number"
                          inputMode="decimal"
                          data-key={c.key}
                          min={c.min ?? undefined}
                          max={c.max ?? undefined}
                          step={c.step ?? undefined}
                        />
                        {c.unit ? <em>{c.unit}</em> : null}
                      </span>
                    </label>
                  ) : Array.isArray(c.options) ? (
                    <div
                      className="ask-seg"
                      data-key={c.key}
                      role="group"
                      aria-label={c.label}
                      key={c.key}
                    >
                      {c.options.map((o) => (
                        <button
                          type="button"
                          key={o.value}
                          data-val={o.value}
                          aria-pressed="false"
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  ) : null,
                )}
              </div>

              {controls.note ? (
                <p className="ask-note">{controls.note}</p>
              ) : null}
              <p className="uhfig-ans" aria-live="polite" />
            </div>
          ) : null}

          <div
            className="uhfig-plot"
            role="img"
            aria-label={describe(meta.alt) ?? meta.title ?? undefined}
          />
          {meta.source ? <p className="uhfig-src">{meta.source}</p> : null}
        </div>
      </div>
      {meta.caption ? (
        <figcaption className={styles.caption}>{meta.caption}</figcaption>
      ) : null}
      {describe(meta.alt) ? (
        <p className={styles.alt}>{describe(meta.alt)}</p>
      ) : null}
    </figure>
  );
};

export default AskFigure;
